import { CdkDragDrop } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { WizDraggableItem } from '../../../directives/behavior/drag-drop/wiz-drag-drop.model';
import { WizDragDropContainer } from './../../../directives/behavior/drag-drop/wiz-drag-drop.directive';
import {
  WizDraggableComponent,
  WizDropItemEvent,
  WIZ_DRAGGABLE_COMPONENT,
} from './../../../directives/behavior/drag-drop/wiz-drag-drop.model';
import { WizActionObj } from './../../../obj/navigation/wiz-action.obj';
import { WizDatasource } from './../../../services/collections/wiz-datasource';
import { WizTabButton } from './../wiz-tab-button/wiz-tab-button';
import { WIZ_EVENT_TYPE } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../events/wiz-event';
import { WizEventManager } from '../../../events/wiz-event.manager';

/**
 * Composant tab bar
 */
@Component({
  selector: 'wiz-tab-bar',
  templateUrl: './wiz-tab-bar.html',
  styleUrls: ['./wiz-tab-bar.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: WIZ_DRAGGABLE_COMPONENT,
      useExisting: forwardRef(() => WizTabBar),
    },
  ],
})
export class WizTabBar implements OnInit, OnDestroy, AfterViewInit, WizDraggableComponent {
  @ViewChild(WizDragDropContainer)
  dragDropContainer: WizDragDropContainer;

  // Div conteneur principal des bouton (hors navigation)
  @ViewChild('tabContainer', { read: ElementRef, static: true })
  tabContainer: ElementRef;

  // Tous les boutons créés
  @ViewChildren(WizTabButton, { read: WizTabButton })
  tabButtons: QueryList<WizTabButton>;

  // Div englobant le container de boutons d'actions
  @ViewChild('divButtonContainer', { read: ElementRef, static: true })
  divButtonContainer: ElementRef;



  private get _divButtonsContainerElt(): HTMLDivElement {
    return this.divButtonContainer.nativeElement;
  }

  private get _tabsElements(): Element[] {
    return Array.from(this._divButtonsContainerElt.children);
  }

  private get _currentScrollLeft(): number {
    return this.tabContainer.nativeElement.scrollLeft;
  }

  private _tabsFullWidth: number = NaN;

  get tabsFullWidth(): number {
    if (isNaN(this._tabsFullWidth)) {
      this._tabsFullWidth = Array.from(this._divButtonsContainerElt.children).reduce<number>(
        (length, child) => length + child.clientWidth,
        0
      );
    }
    return this._tabsFullWidth;
  }

  /** map d'association action obj => wiztabbutton */
  private _actionTabButtons: Map<WizActionObj, WizTabButton>;

  @Input()
  showNavigation: boolean = true;

  // orientation
  @Input() orientation: string = 'row'; // column

  // rendu en colonnes
  public get columnMode(): boolean {
    return this.orientation === 'column';
  }

  public get overflow(): string {
    return this.orientation === "column" ? 'auto' : 'hidden';
  }

  @Input()
  dragEnabled: boolean = true;

  @Input()
  dropEnabled: boolean = true;

  @Input()
  set dragDropEnabled(pValue: boolean) {
    this.dragEnabled = this.dropEnabled = pValue;
  }

  /** Liste des boutons à afficher */
  private _wizButtonList: WizDatasource<WizActionObj>;

  @Input()
  public set wizButtonList(pActionList: WizDatasource<WizActionObj>) {
    this._wizButtonList = pActionList;

    if (this._wizButtonList) {
      this._sub$.add(
        this._wizButtonList.changes.subscribe((changes) => {
          if (changes.type === 'selectionChanged') {
            const selectionEvent: WizEvent = new WizEvent(WIZ_EVENT_TYPE.SELECTION_CHANGED, null, this.selectedTab);
            WizEventManager.dispatchEvent(this.tabContainer, selectionEvent);
          }
          if (changes.type === 'programmaticChanged' && this._wizButtonList.selectedItem) {
            // Attente du refresh écran (dessin des tabs suite au refresh de la wizDataSource)
            setTimeout(() => {
              this._ensureSelectedIsVisible();
              this.cdref.markForCheck();
            });
          } else {
            this.cdref.markForCheck();
          }
        })
      );
    }
  }

  get wizButtonList(): WizDatasource<WizActionObj> {
    return this._wizButtonList;
  }

  private _selectedTabIndex: number;

  private _sub$: Subscription = new Subscription();

  /**
   * Constructeur avec injction du renderer et changedetectorref
   * @param cdref changedetectorref
   * @param renderer : rendu
   */
  constructor(protected cdref: ChangeDetectorRef, public elementRef: ElementRef, public renderer: Renderer2) {}

  /**
   * init : construction de la liste d'actions et listener clic
   */
  public ngOnInit(): void {
    WizEventManager.addListener(this, this.tabContainer, WIZ_EVENT_TYPE.CLICK, this.onClick, { capture: true });
    WizEventManager.addListener(this, this.tabContainer, WIZ_EVENT_TYPE.SELECTION_CHANGING, this.onSelectionChanging);
  }

  /**
   * Suppression du listener clic
   */
  public ngOnDestroy(): void {
    WizEventManager.removeListener(this, this.tabContainer, WIZ_EVENT_TYPE.CLICK);
    WizEventManager.removeListener(this, this.tabContainer, WIZ_EVENT_TYPE.SELECTION_CHANGING);
    this._sub$.unsubscribe();
  }

  /**
   * Observer du container de bouton (dont la taille évolue en fonction des boutons)
   */
  private $roButtonContainer: ResizeObserver;

  ngAfterViewInit(): void {
    this.$roButtonContainer = new ResizeObserver(() => this.cdref.markForCheck());
    this.$roButtonContainer.observe(this._divButtonsContainerElt);

    this._buildActionTabButtonsMap();
    if (this.tabButtons) {
      this._sub$.add(
        this.tabButtons.changes.subscribe(() => {
          this._buildActionTabButtonsMap();
          this.cdref.markForCheck();
        })
      );
    }
  }

  private _buildActionTabButtonsMap() {
    this._tabsFullWidth = NaN;
    this._actionTabButtons = new Map();
    this.tabButtons?.forEach((tab) => {
      this._actionTabButtons.set(tab.actionObj, tab);
    });
  }

  private _ensureSelectedIsVisible() {
    if (this._wizButtonList.selectedItem) {
      const itemIndex: number = this._wizButtonList.getItemIndex(this._wizButtonList.selectedItem);
      if (!this.isTabVisible(itemIndex) && this._selectedTabIndex !== itemIndex) {
        this._scrollToTab(itemIndex);
      }
    }
  }

  /**
   * Scroll arrière d'un bouton
   */
  public scrollBack(): void {
    const tabs: Element[] = this._tabsElements;
    let xIndex = 0;
    for (let index = 0; index < tabs.length; index++) {
      if (xIndex >= this._currentScrollLeft) {
        this._scrollToTab(index - 1);
        return;
      }
      xIndex += tabs[index]?.clientWidth;
    }
  }

  /**
   * Scroll avant d'un bouton
   */
  public scrollForward(): void {
    const tabs: Element[] = this._tabsElements;
    let xIndex = 0;
    for (let index = 0; index < tabs.length; index++) {
      xIndex += tabs[index]?.clientWidth;
      if (xIndex > this._currentScrollLeft + this._divButtonsContainerElt.clientWidth) {
        this._scrollToTab(index);
        return;
      }
    }
  }

  /**
   * Scroll arrière jusqu'au premier bouton
   */
  public scrollToFirst(): void {
    this._scrollToTab(0);
  }

  /**
   * Scroll avant jusqu'au dernier bouton
   */
  public scrollToLast(): void {
    this._scrollToTab(this.tabButtons.length - 1);
  }

  private _scrollToTab(pTabIndex: number) {
    this._tabsElements[pTabIndex]?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });
  }

  /**
   * Accessibilité du bouton de scroll arrière
   * @return boolean
   */
  public get isScrollBackDisabled(): boolean {
    return this._currentScrollLeft === 0;
  }

  /**
   * Accessibilité du bouton de scroll avant
   * @return boolean
   */
  public get isScrollForwardDisabled(): boolean {
    return this._currentScrollLeft + this._divButtonsContainerElt.clientWidth >= this.tabsFullWidth;
  }

  public isTabVisible(pTabIndex: number): boolean {
    return (
      this._divButtonsContainerElt.scrollWidth <= pTabIndex &&
      pTabIndex <= this._divButtonsContainerElt.scrollWidth + this._divButtonsContainerElt.clientWidth
    );
  }

  // private _getTabX(pTab: WizTabButton): number {
  //   const indexTab: number = this._wizButtonList.getItemIndex(pTab.actionObj);
  //   const tabs = Array.from(this._divButtonsContainerElt.children);
  //   let tabX = 0;
  //   for (let index: number = 0; index < indexTab; index++) {
  //     tabX += tabs[index].clientWidth;
  //   }
  //   return tabX;
  // }

  public doNav(action: string): void {
    switch (action) {
      // Demande d'ajout d'onglet
      case 'add':
        const addEvent: WizEvent = new WizEvent(WIZ_EVENT_TYPE.ADD);
        WizEventManager.dispatchEvent(this.elementRef, addEvent);
        break;
      // Les scrolls
      case 'previous':
        this.scrollBack();
        break;
      case 'next':
        this.scrollForward();
        break;
      case 'first':
        this.scrollToFirst();
        break;
      case 'last':
        this.scrollToLast();
        break;
    }
  }

  /**
   * Handler de clic sur un des bouton
   * @param e clic
   */
  private onClick(e: WizEvent): void {
    if (e.component instanceof WizTabButton) {
      const tab: WizTabButton = e.component as WizTabButton;
      this.askSelectTab(tab);
    }
  }

  private get selectedTab(): WizTabButton {
    return this._actionTabButtons?.get(this._wizButtonList.selectedItem);
  }

  /**
   * Demande de sélection d'un onglet
   * @param tab bouton sélectionné
   */
  private askSelectTab(tab: WizTabButton) {
    if (tab) {
      if (tab.actionObj !== this._wizButtonList.selectedItem) {
        const selectionEvent: WizEvent = new WizEvent(WIZ_EVENT_TYPE.SELECTION_CHANGING, null, {
          previous: this.selectedTab,
          next: tab,
        });
        WizEventManager.dispatchEvent(this.tabContainer, selectionEvent);
      }
    }
  }

  private onSelectionChanging(pEvent: WizEvent): void {
    if (!pEvent.defaultPrevented) {
      this._wizButtonList.toggle(pEvent.value.next.actionObj);
    }
  }

  isSelected(obj: WizActionObj): boolean {
    return this._wizButtonList.isSelected(obj);
  }

  onDropButton({ previousIndex, currentIndex, ...data }: CdkDragDrop<any>) {
    if (previousIndex !== currentIndex) {
      this._wizButtonList = this._wizButtonList.moveItem(previousIndex, currentIndex).clone(true, true);
    }
  }

  onTabMouseDown(event: MouseEvent, data: WizActionObj) {
    this.dragDropContainer.startDrag({
      target: event.currentTarget,
      data,
    });
  }

  /** Fonction de contrôle sur dragOver param = item return true si drop autorisé */
  checkDragOver(pEvent: WizDropItemEvent): boolean {
    // A surcharger au besoin
    return true;
  }

  /** Ajout d'un élément suite à un drop sur ce composant */
  onAddItemAfterDrag(pEvent: WizDropItemEvent): void {
    // Pas de gestion de drag/drop externe
  }

  /** Déplacement d'un élément après drop (cas drag/drop en interne) */
  onMoveItemAfterDrag({ item, targetPosition }: WizDropItemEvent): void {
    // this.wizButtonList.getWorkedSource().forEach((wao: WizActionObj) =>{
    //       console.log('onMoveItemAfterDrag before', wao.buttonCode, wao.order);
    // });
    const currentIndex: number = this.wizButtonList.getItemIndex(item.data as WizActionObj);
    //console.log("onMoveItemAfterDrag", item, currentIndex, targetPosition);
    let realTarget: number = targetPosition;
    if(targetPosition > currentIndex && targetPosition !== this.wizButtonList.length - 1) {
      realTarget = targetPosition - 1;
    }
    this.wizButtonList.moveItem(
      currentIndex,
      realTarget
    );

    // this.wizButtonList.getWorkedSource().forEach((wao: WizActionObj) =>{
    //       console.log('onMoveItemAfterDrag after', wao.buttonCode, wao.order);
    // });
  }

  /** Suppression d'un élément car drop dans un autre container */
  onRemoveItemAfterDrag(pItem: WizDraggableItem): void {
    // Pas de gestion de drag/drop externe
  }
}
